import { referenceDisplay } from '@seen/core/display'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import type { MedicationRefillTask } from '@seen/core/models/tasks/standard/medicationRefillTask'
import { extractId } from '@seen/core/utils'
import { type ReactNode, useCallback, useMemo } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import { MedicationRefillRow } from '#src/components/medications/MedicationRefillRow.tsx'
import { useElationDraftPrescription } from '#src/hooks/useElationDraftPrescription.ts'
import {
  combineDraftIdsByThread,
  medicationForDraftOpen,
  useMedicationDraftsByThread,
} from '#src/hooks/useMedicationDraftsByThread.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface MedicationRefillCardProps {
  task: MedicationRefillTask
}

export function MedicationRefillCard({
  task,
}: MedicationRefillCardProps): ReactNode {
  const ids = task.medicationRequestRefs
    .map((ref) => extractId(ref))
    .filter((id): id is string => Boolean(id))
  const participantId = extractId(task.participant)

  const { data: medications } = useSearch(
    ExtendedMedicationRequest,
    ids.length > 0 ? ExtendedMedicationRequest.queryByIds(ids) : undefined
  )
  const {
    createDraftPrescription,
    openDraftPrescription,
    draftingMedicationIds,
    optimisticDraftIdsByThreadId,
    canDraft,
  } = useElationDraftPrescription(participantId, { surface: 'refill_task' })
  const { draftByThreadId } = useMedicationDraftsByThread(task.participant)
  const openStampedDraft = useCallback(
    (draftId: number | string, medication?: ExtendedMedicationRequest) => {
      openDraftPrescription(
        draftId,
        medicationForDraftOpen({
          draftId,
          medication,
          draftsByThreadId: draftByThreadId,
        })
      )
    },
    [draftByThreadId, openDraftPrescription]
  )
  const draftIdsByThreadId = useMemo(
    () =>
      combineDraftIdsByThread({
        serverDraftsByThreadId: draftByThreadId,
        optimisticDraftIdsByThreadId,
      }),
    [draftByThreadId, optimisticDraftIdsByThreadId]
  )

  if (ids.length === 0) {
    return null
  }

  const byId = new Map(
    (medications ?? []).flatMap((med) =>
      med.id ? [[med.id, med] as const] : []
    )
  )

  return (
    <Card className="flex flex-col gap-2">
      {ids.map((id) => {
        const medication = byId.get(id)
        const display =
          medication?.medicationDisplay ??
          referenceDisplay(
            task.medicationRequestRefs.find((ref) => extractId(ref) === id)
          ) ??
          'Medication'
        return (
          <MedicationRefillRow
            key={id}
            display={display}
            href={
              participantId
                ? `/Patient/${participantId}/medications/${id}`
                : undefined
            }
            medication={medication}
            onDraftPrescription={canDraft ? createDraftPrescription : undefined}
            onOpenDraftPrescription={openStampedDraft}
            draftIdsByThreadId={draftIdsByThreadId}
            draftingMedicationIds={draftingMedicationIds}
          />
        )
      })}
    </Card>
  )
}
